import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import './ClubView.css';

/* ─────────────────────────────────────────────────────────────
   CLUB VIEW PAGE — /clubs/:id
   Shows a club's leaderboard, match history, member list, and
   (for admins) club settings.

   Access control: if the current user is not a member, they
   are immediately redirected to /clubs.
   ───────────────────────────────────────────────────────────── */

/* ── Helpers ──────────────────────────────────────────────── */

function timeAgo(dateStr) {
  const diff  = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)    return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4)   return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function formatLabel(format) {
  const labels = { bo1: 'Single game', bo3: 'Best of 3', bo5: 'Best of 5', bo7: 'Best of 7' };
  return labels[format] || format;
}

function CrownIcon() {
  return (
    <svg className="cv-crown" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M5 16l-3-9 5.5 4L12 3l4.5 8L22 7l-3 9H5zm0 2h14v2H5v-2z"/>
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════ */

function ClubView() {
  const { id }       = useParams();
  const { user }     = useAuth();
  const navigate     = useNavigate();

  // ── Club metadata + my role ────────────────────────────────
  const [club,          setClub]          = useState(null);
  const [myRole,        setMyRole]        = useState(null); // 'admin' | 'member'
  const [memberCount,   setMemberCount]   = useState(0);
  const [pageLoading,   setPageLoading]   = useState(true);

  // ── Copy invite link ───────────────────────────────────────
  const [copied, setCopied] = useState(false);

  // ── Active tab ─────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('leaderboard');

  // ── Leaderboard tab state ──────────────────────────────────
  const [lbMembers,     setLbMembers]     = useState(null); // null = not loaded yet

  // ── Match History tab state ────────────────────────────────
  const [matches,       setMatches]       = useState(null);

  // ── Members tab state ──────────────────────────────────────
  const [members,       setMembers]       = useState(null);
  const [removingId,    setRemovingId]    = useState(null);
  const [leaving,       setLeaving]       = useState(false);

  // ── Settings tab state ─────────────────────────────────────
  const [editName,      setEditName]      = useState('');
  const [editDesc,      setEditDesc]      = useState('');
  const [editPublic,    setEditPublic]    = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [saveMsg,       setSaveMsg]       = useState('');
  const [regenLoading,  setRegenLoading]  = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting,      setDeleting]      = useState(false);
  const [codeJustCopied, setCodeJustCopied] = useState(false);

  // ── Load club + check membership ───────────────────────────
  const loadClub = useCallback(async () => {
    if (!user) return;

    // Fetch club metadata
    const { data: clubData, error: clubErr } = await supabase
      .from('clubs')
      .select('id, name, description, is_public, join_code, created_by')
      .eq('id', id)
      .single();

    if (clubErr || !clubData) {
      navigate('/clubs', { replace: true });
      return;
    }

    // Check caller is a member
    const { data: mem } = await supabase
      .from('club_memberships')
      .select('role')
      .eq('club_id', id)
      .eq('user_id', user.id)
      .maybeSingle();

    if (!mem) {
      // Not a member — redirect to hub
      navigate('/clubs', { replace: true });
      return;
    }

    // Fetch member count
    const { count } = await supabase
      .from('club_memberships')
      .select('*', { count: 'exact', head: true })
      .eq('club_id', id);

    setClub(clubData);
    setMyRole(mem.role);
    setMemberCount(count || 0);
    setEditName(clubData.name);
    setEditDesc(clubData.description || '');
    setEditPublic(clubData.is_public);
    setPageLoading(false);
  }, [id, user, navigate]);

  useEffect(() => {
    loadClub();
  }, [loadClub]);

  // ── Lazy-load tab data ─────────────────────────────────────
  useEffect(() => {
    if (!club) return;
    if (activeTab === 'leaderboard' && lbMembers === null)   fetchLeaderboard();
    if (activeTab === 'history'     && matches === null)      fetchMatchHistory();
    if (activeTab === 'members'     && members === null)      fetchMembers();
  }, [activeTab, club]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Leaderboard: members ranked by global ELO ─────────────
  async function fetchLeaderboard() {
    const { data: memRows } = await supabase
      .from('club_memberships')
      .select('user_id')
      .eq('club_id', id);

    const ids = (memRows || []).map(m => m.user_id);
    if (!ids.length) { setLbMembers([]); return; }

    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, username, elo_rating, wins, losses, matches_played, unique_opponents_count')
      .in('id', ids)
      .order('elo_rating', { ascending: false });

    setLbMembers(profiles || []);
  }

  // ── Match History: both players must be club members ───────
  async function fetchMatchHistory() {
    const { data } = await supabase.rpc('get_club_match_history', {
      p_club_id: id,
      p_limit:   50,
    });
    setMatches(data || []);
  }

  // ── Members: list with roles ───────────────────────────────
  async function fetchMembers() {
    const { data } = await supabase
      .from('club_memberships')
      .select('role, joined_at, user_id, profiles(id, username, elo_rating)')
      .eq('club_id', id)
      .order('joined_at', { ascending: true });
    setMembers(data || []);
  }

  // ── Copy invite link ───────────────────────────────────────
  function copyInviteLink() {
    const link = `${window.location.origin}/clubs?join=${club.join_code}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // ── Remove a member (admin only) ───────────────────────────
  async function handleRemoveMember(memberId) {
    setRemovingId(memberId);
    const { error } = await supabase.rpc('remove_club_member', {
      p_club_id:   id,
      p_member_id: memberId,
    });
    if (!error) {
      setMembers(prev => prev.filter(m => m.user_id !== memberId));
      setMemberCount(c => c - 1);
    }
    setRemovingId(null);
  }

  // ── Leave club ─────────────────────────────────────────────
  async function handleLeave() {
    if (leaving) return;
    setLeaving(true);
    const { error } = await supabase.rpc('leave_club', { p_club_id: id });
    if (error) {
      alert(error.message);
      setLeaving(false);
      return;
    }
    window.dispatchEvent(new CustomEvent('ranktt:clubResolved'));
    navigate('/clubs', { replace: true });
  }

  // ── Save club settings ─────────────────────────────────────
  async function handleSaveSettings(e) {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setSaveMsg('');

    const { error } = await supabase.rpc('update_club', {
      p_club_id:     id,
      p_name:        editName.trim(),
      p_description: editDesc.trim() || null,
      p_is_public:   editPublic,
    });

    if (error) {
      setSaveMsg(error.message || 'Failed to save. Please try again.');
    } else {
      setClub(prev => ({
        ...prev,
        name:        editName.trim(),
        description: editDesc.trim() || null,
        is_public:   editPublic,
      }));
      setSaveMsg('saved');
      setTimeout(() => setSaveMsg(''), 2500);
    }
    setSaving(false);
  }

  // ── Regenerate join code ───────────────────────────────────
  async function handleRegenCode() {
    if (!window.confirm('This will invalidate the old link immediately. Anyone with the old link will not be able to join. Continue?')) return;
    setRegenLoading(true);
    const { data: newCode, error } = await supabase.rpc('regenerate_join_code', { p_club_id: id });
    if (!error && newCode) {
      setClub(prev => ({ ...prev, join_code: newCode }));
    }
    setRegenLoading(false);
  }

  // ── Copy just the code ─────────────────────────────────────
  function copyCode() {
    navigator.clipboard.writeText(club.join_code).then(() => {
      setCodeJustCopied(true);
      setTimeout(() => setCodeJustCopied(false), 2000);
    });
  }

  // ── Delete club ────────────────────────────────────────────
  async function handleDeleteClub() {
    if (deleteConfirm !== club.name) return;
    setDeleting(true);
    const { error } = await supabase.rpc('delete_club', { p_club_id: id });
    if (error) {
      alert(error.message);
      setDeleting(false);
      return;
    }
    window.dispatchEvent(new CustomEvent('ranktt:clubResolved'));
    navigate('/clubs', { replace: true });
  }

  /* ── Render ──────────────────────────────────────────────── */

  if (pageLoading) {
    return (
      <div className="cv-page">
        <div className="cv-container">
          <div className="cv-loading">
            <span className="cv-loading-star">★</span>
            <span className="cv-loading-star">★</span>
            <span className="cv-loading-star">★</span>
          </div>
        </div>
      </div>
    );
  }

  if (!club) return null;

  const inviteLink = `${window.location.origin}/clubs?join=${club.join_code}`;

  return (
    <div className="cv-page">
      <div className="cv-container">

        {/* Back link */}
        <Link to="/clubs" className="cv-back">← Clubs</Link>

        {/* ── Club header ──────────────────────────────────── */}
        <div className="cv-header">
          <div className="cv-header-top">
            <div className="cv-header-info">
              <h1 className="cv-club-name">{club.name}</h1>
              <div className="cv-header-badges">
                <span className={`cv-vis-badge${club.is_public ? ' cv-vis-public' : ''}`}>
                  {club.is_public ? '🌐 Public' : '🔒 Private'}
                </span>
                <span className="cv-member-count">
                  {memberCount} {memberCount === 1 ? 'member' : 'members'}
                </span>
              </div>
            </div>
            <button className="cv-invite-btn" onClick={copyInviteLink}>
              {copied ? '✓ Copied!' : '🔗 Share Invite'}
            </button>
          </div>
          {club.description && (
            <p className="cv-club-desc">{club.description}</p>
          )}
        </div>

        {/* ── Tabs ─────────────────────────────────────────── */}
        <div className="friends-tabs">
          <button
            className={`friends-tab${activeTab === 'leaderboard' ? ' active' : ''}`}
            onClick={() => setActiveTab('leaderboard')}
          >
            Leaderboard
          </button>
          <button
            className={`friends-tab${activeTab === 'history' ? ' active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Match History
          </button>
          <button
            className={`friends-tab${activeTab === 'members' ? ' active' : ''}`}
            onClick={() => setActiveTab('members')}
          >
            Members
            <span className="friends-tab-count">{memberCount}</span>
          </button>
          {myRole === 'admin' && (
            <button
              className={`friends-tab${activeTab === 'settings' ? ' active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              Settings
            </button>
          )}
        </div>

        {/* ── TAB: Leaderboard ─────────────────────────────── */}
        {activeTab === 'leaderboard' && (
          <div className="cv-tab-body">
            {lbMembers === null ? (
              <div className="cv-loading">
            <span className="cv-loading-star">★</span>
            <span className="cv-loading-star">★</span>
            <span className="cv-loading-star">★</span>
          </div>
            ) : lbMembers.length === 0 ? (
              <p className="cv-empty">No members yet.</p>
            ) : (
              <div className="cv-lb-list">
                {lbMembers.map((p, i) => (
                  <Link key={p.id} to={`/profile/${p.username}`} className="cv-lb-row">
                    <span className={`cv-lb-rank${i === 0 ? ' cv-lb-rank-gold' : ''}`}>
                      {i === 0 ? <CrownIcon /> : `#${i + 1}`}
                    </span>
                    <span className="cv-lb-username">
                      {p.username}
                      {p.id === user.id && <span className="cv-lb-you"> (you)</span>}
                    </span>
                    <span className="cv-lb-elo">{p.elo_rating}</span>
                    <span className="cv-lb-record">
                      {p.wins}W – {p.losses}L
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TAB: Match History ───────────────────────────── */}
        {activeTab === 'history' && (
          <div className="cv-tab-body">
            {matches === null ? (
              <div className="cv-loading">
            <span className="cv-loading-star">★</span>
            <span className="cv-loading-star">★</span>
            <span className="cv-loading-star">★</span>
          </div>
            ) : matches.length === 0 ? (
              <div className="cv-empty-box">
                <p className="cv-empty-title">No matches yet</p>
                <p className="cv-empty-sub">
                  Matches played between club members will appear here once confirmed.
                </p>
              </div>
            ) : (
              <div className="cv-match-list">
                {matches.map(m => (
                  <div key={m.match_id} className="cv-match-row">
                    <div className="cv-match-players">
                      <Link to={`/profile/${m.winner_username}`} className="cv-match-winner">
                        {m.winner_username}
                      </Link>
                      <span className="cv-match-vs">vs</span>
                      <Link to={`/profile/${m.loser_username}`} className="cv-match-loser">
                        {m.loser_username}
                      </Link>
                    </div>
                    <div className="cv-match-score">
                      {m.winner_games_won}–{m.loser_games_won}
                    </div>
                    <div className="cv-match-meta">
                      <span className="cv-match-format">{formatLabel(m.format)}</span>
                      <span className="cv-match-time">{timeAgo(m.played_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TAB: Members ─────────────────────────────────── */}
        {activeTab === 'members' && (
          <div className="cv-tab-body">
            {members === null ? (
              <div className="cv-loading">
            <span className="cv-loading-star">★</span>
            <span className="cv-loading-star">★</span>
            <span className="cv-loading-star">★</span>
          </div>
            ) : (
              <div className="cv-member-list">
                {members.map(m => (
                  <div key={m.user_id} className="cv-member-row">
                    <div className="cv-member-avatar">
                      {m.profiles?.username?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="cv-member-info">
                      <Link
                        to={`/profile/${m.profiles?.username}`}
                        className="cv-member-username"
                      >
                        {m.profiles?.username}
                        {m.user_id === user.id && <span className="cv-lb-you"> (you)</span>}
                      </Link>
                      <span className="cv-member-elo">
                        {m.profiles?.elo_rating} ELO
                      </span>
                    </div>
                    {m.role === 'admin' && (
                      <span className="cv-role-badge cv-role-admin">Admin</span>
                    )}

                    {/* Admin can remove non-admin members who aren't themselves */}
                    {myRole === 'admin' && m.role !== 'admin' && m.user_id !== user.id && (
                      <button
                        className="cv-remove-btn"
                        onClick={() => handleRemoveMember(m.user_id)}
                        disabled={removingId === m.user_id}
                      >
                        {removingId === m.user_id ? '…' : 'Remove'}
                      </button>
                    )}
                  </div>
                ))}

                {/* Leave club button — admins must transfer ownership or delete instead */}
                <div className="cv-leave-row">
                  {myRole === 'admin' ? (
                    <p className="cv-admin-leave-note">
                      Admins cannot leave. Transfer ownership or delete the club in Settings.
                    </p>
                  ) : (
                    <button
                      className="cv-leave-btn"
                      onClick={handleLeave}
                      disabled={leaving}
                    >
                      {leaving ? 'Leaving…' : 'Leave Club'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── TAB: Settings (admin only) ───────────────────── */}
        {activeTab === 'settings' && myRole === 'admin' && (
          <div className="cv-tab-body cv-settings">

            {/* Edit Club Info */}
            <form className="cv-settings-card" onSubmit={handleSaveSettings}>
              <h3 className="cv-settings-title">Club Info</h3>

              <div className="cc-field">
                <label className="cc-label" htmlFor="cv-edit-name">Club Name</label>
                <input
                  id="cv-edit-name"
                  className="cc-input"
                  type="text"
                  maxLength={60}
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  required
                />
              </div>

              <div className="cc-field">
                <label className="cc-label" htmlFor="cv-edit-desc">
                  Description <span className="cc-optional">(optional)</span>
                </label>
                <textarea
                  id="cv-edit-desc"
                  className="cc-textarea"
                  maxLength={280}
                  rows={3}
                  value={editDesc}
                  onChange={e => setEditDesc(e.target.value)}
                />
              </div>

              <div className="cc-field">
                <div className="cc-toggle-row">
                  <div className="cc-toggle-labels">
                    <span className="cc-label">Visibility</span>
                    <span className="cc-toggle-value">
                      {editPublic ? 'Public' : 'Private'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`lb-toggle${editPublic ? ' lb-toggle-on' : ''}`}
                    onClick={() => setEditPublic(v => !v)}
                    aria-label="Toggle club visibility"
                  >
                    <span className="lb-toggle-knob" />
                  </button>
                </div>
                <p className="cc-toggle-hint">
                  {editPublic
                    ? 'Anyone can find and join this club by searching the Clubs hub.'
                    : 'Only people with your join code or invite link can join.'}
                </p>
              </div>

              <div className="cv-save-row">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={saving || !editName.trim()}
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
                {saveMsg === 'saved' && (
                  <span className="cv-save-success">✓ Saved</span>
                )}
                {saveMsg && saveMsg !== 'saved' && (
                  <span className="cv-save-error">{saveMsg}</span>
                )}
              </div>
            </form>

            {/* Join Code */}
            <div className="cv-settings-card">
              <h3 className="cv-settings-title">Join Code</h3>
              <p className="cv-settings-sub">
                Share this code or the invite link with anyone you want to join.
              </p>

              <div className="cv-code-row">
                <code className="cv-join-code">{club.join_code}</code>
                <button className="cv-copy-code-btn" onClick={copyCode}>
                  {codeJustCopied ? '✓ Copied' : 'Copy Code'}
                </button>
                <button className="cv-copy-code-btn" onClick={copyInviteLink}>
                  {copied ? '✓ Copied Link' : 'Copy Link'}
                </button>
              </div>

              <div className="cv-regen-row">
                <button
                  className="cv-regen-btn"
                  onClick={handleRegenCode}
                  disabled={regenLoading}
                >
                  {regenLoading ? 'Regenerating…' : 'Regenerate Code'}
                </button>
                <span className="cv-regen-hint">
                  Invalidates the old link immediately.
                </span>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="cv-settings-card cv-danger-zone">
              <h3 className="cv-settings-title cv-danger-title">Danger Zone</h3>
              <p className="cv-settings-sub">
                Permanently delete this club and remove all members. This cannot be undone.
              </p>

              <div className="cv-delete-confirm">
                <label className="cc-label">
                  Type <strong>{club.name}</strong> to confirm
                </label>
                <input
                  className="cc-input"
                  type="text"
                  placeholder={club.name}
                  value={deleteConfirm}
                  onChange={e => setDeleteConfirm(e.target.value)}
                />
                <button
                  className="cv-delete-btn"
                  onClick={handleDeleteClub}
                  disabled={deleteConfirm !== club.name || deleting}
                >
                  {deleting ? 'Deleting…' : 'Permanently Delete Club'}
                </button>
              </div>
            </div>

          </div>
        )}

      </div>
    </div>
  );
}

export default ClubView;
